import { BaseComponentImpl } from '../../../../types/implementations';
import { Building } from '../../../../types/protocols';
import { ComponentLifetime, MaintenanceTemplate, TemplatesByType, getIntervalForDirection } from '../../TemplatesEditor/types';
import { formatDateForStorage } from '../../../../Helpers/DateUtils';
import { wellFormedComponents } from '../../../../Helpers/componentIntegrity';
import { isConditionSet } from '../../../../Helpers/conditionUtils';
import type { ComponentMaintenanceData, MaintenanceActivity, MaintenancePlan } from '../../../../Helpers/maintenanceCostUtils';
import { MaintenanceDraftWithDone } from '../types';
import { calculateReplacementDate } from './calculations';
import { resolveCardinalDirection } from './cardinalDirectionResolver';
import { newComponentEntry, syncDraftWithBuilding } from './draftSync';

/**
 * A new draft that starts from the active plan ("Utgå från aktiv plan").
 *
 * The active plan is a snapshot: activation writes every date, interval and
 * template cost resolved, so what the user decided and what merely followed a
 * template look alike. Only decisions are carried over, so a draft made today
 * follows today's templates and conditions where the user never chose otherwise:
 *
 * - a cost is copied only when its source is `custom` (with comment and attachments)
 * - the next date is copied (it carries agreed timing and completed work orders)
 * - an interval is copied only when it differs from the template's for the
 *   component's direction today
 * - a replacement year is copied only when it differs from what the condition
 *   gives today (both pulled to this year when overdue)
 * - removed activities, investment percentages, custom quantities and the
 *   completion history (`lastCompleted*`) are copied
 * - `isDone` is not copied: the new round is reviewed from scratch
 *
 * Activation drops excluded components, so they are recovered: a listed
 * component missing from the active plan that existed when it was activated
 * (`createdWhen` before `activatedAt`) was excluded and is again. One created
 * later is new and is left to the draft sync, which adds it included.
 *
 * Entries are then completed by `syncDraftWithBuilding`, so the draft is stored as the
 * draft page would sync it and loading it adds nothing.
 */

export interface DraftFromActiveOptions {
    active: MaintenancePlan;
    building: Building;
    componentLifetimes: Record<string, ComponentLifetime>;
    maintenanceTemplates: TemplatesByType;
    findTemplate: (templateId: string) => MaintenanceTemplate | undefined;
    now?: Date;
}

export const draftFromActivePlan = (options: DraftFromActiveOptions): MaintenanceDraftWithDone => {
    const { active, building, componentLifetimes, maintenanceTemplates, findTemplate } = options;
    const now = options.now ?? new Date();
    const currentYear = now.getFullYear();
    const activatedAt = active.metadata?.activatedAt;
    const activeComponents = active.components || {};

    const components: Record<string, ComponentMaintenanceData> = {};

    wellFormedComponents(building.model?.structure?.components)
        .filter(data => !data.archivedAt && data.specificType?.value)
        .forEach(componentData => {
            const component = new BaseComponentImpl(componentData);
            const componentType = component.specificType!.value!;
            const lifetime = componentLifetimes[componentType];
            const typeTemplates = maintenanceTemplates[componentType] || [];
            const stored = activeComponents[component.key];

            if (stored) {
                components[component.key] = copyDecisions(stored, component, lifetime, building, typeTemplates, findTemplate, currentYear, now);
                return;
            }

            // Same gate as the draft sync: nothing to plan without a condition and a lifetime
            if (!isConditionSet(component.condition?.value?.value) || !lifetime) return;
            if (activatedAt && component.createdWhen && component.createdWhen < activatedAt) {
                components[component.key] = { ...newComponentEntry(component, lifetime, building, typeTemplates, now), excluded: true };
            }
        });

    const seeded: MaintenanceDraftWithDone = {
        components,
        metadata: {
            savedAt: formatDateForStorage(now),
            buildingKey: building.key
        }
    };

    // Complete the entries as the draft page's sync would: the database drops an empty
    // object, so an entry left with nothing in it (no activities, replacement year
    // following the condition) would not be stored and come back as "tillkom" on load
    return syncDraftWithBuilding({ draft: seeded, building, componentLifetimes, maintenanceTemplates, findTemplate, now }).draft;
};

const copyDecisions = (
    stored: ComponentMaintenanceData,
    component: BaseComponentImpl,
    lifetime: ComponentLifetime | undefined,
    building: Building,
    typeTemplates: MaintenanceTemplate[],
    findTemplate: (templateId: string) => MaintenanceTemplate | undefined,
    currentYear: number,
    now: Date
): ComponentMaintenanceData => {
    const entry: ComponentMaintenanceData = { maintenanceActivities: {} };

    if (typeof stored.replacementYear === 'number' && isFinite(stored.replacementYear)) {
        const derived = lifetime ? calculateReplacementDate(component, lifetime, building)?.getFullYear() : undefined;
        const storedDue = Math.max(stored.replacementYear, currentYear);
        if (derived === undefined || Math.max(derived, currentYear) !== storedDue) {
            entry.replacementYear = storedDue;
        }
    }
    if (stored.replacementCostSource === 'custom' && typeof stored.replacementCost === 'number') {
        entry.replacementCostSource = 'custom';
        entry.replacementCost = stored.replacementCost;
    } else if (stored.replacementCostSource === 'customRate' && typeof stored.replacementCustomUnitPrice === 'number') {
        // The own unit price is the decision; the cost is computed live again
        entry.replacementCostSource = 'customRate';
        entry.replacementCustomUnitPrice = stored.replacementCustomUnitPrice;
    }
    if (stored.replacementCostComment) entry.replacementCostComment = stored.replacementCostComment;
    if (stored.replacementAttachments) entry.replacementAttachments = stored.replacementAttachments;
    if (stored.replacementInvestmentPercentage !== undefined) entry.replacementInvestmentPercentage = stored.replacementInvestmentPercentage;
    if (stored.removedActivities && Object.keys(stored.removedActivities).length > 0) {
        entry.removedActivities = { ...stored.removedActivities };
    }

    const direction = resolveCardinalDirection(component, building);
    Object.entries(stored.maintenanceActivities || {}).forEach(([templateId, activity]) => {
        if (activity.disabled === true) return;
        const template = typeTemplates.find(t => t.id === templateId) ?? findTemplate(templateId);
        if (!template) return; // template deleted since activation

        const templateInterval = getIntervalForDirection(template, direction);
        const sameInterval = activity.intervalType === templateInterval.interval_type
            && activity.intervalValue === templateInterval.interval_value;
        const hasInterval = typeof activity.intervalValue === 'number' && activity.intervalValue > 0 && !!activity.intervalType;

        const copied: MaintenanceActivity = {
            nextDate: activity.nextDate,
            intervalType: hasInterval && !sameInterval ? activity.intervalType : templateInterval.interval_type,
            intervalValue: hasInterval && !sameInterval ? activity.intervalValue : templateInterval.interval_value,
            lastUpdated: formatDateForStorage(now)
        };
        if (activity.costSource === 'custom' && typeof activity.cost === 'number') {
            copied.costSource = 'custom';
            copied.cost = activity.cost;
        } else if (activity.costSource === 'customRate' && typeof activity.customUnitPrice === 'number') {
            // The own unit price is the decision; the cost is computed live again
            copied.costSource = 'customRate';
            copied.customUnitPrice = activity.customUnitPrice;
        }
        if (activity.costComment) copied.costComment = activity.costComment;
        if (activity.costAttachments) copied.costAttachments = activity.costAttachments;
        if (activity.investmentPercentage !== undefined) copied.investmentPercentage = activity.investmentPercentage;
        if (activity.customQuantity !== undefined) copied.customQuantity = activity.customQuantity;
        if (activity.customUnitCount !== undefined) copied.customUnitCount = activity.customUnitCount;
        if (activity.comment) copied.comment = activity.comment;
        Object.assign(copied, completionHistory(activity));
        entry.maintenanceActivities[templateId] = copied;
    });

    return entry;
};

type CompletionHistory = Pick<MaintenanceActivity, 'lastCompleted' | 'lastCompletedBy' | 'lastCompletedWorkOrderKey'>;

const completionHistory = (activity: MaintenanceActivity | undefined): CompletionHistory => {
    const history: CompletionHistory = {};
    if (!activity?.lastCompleted) return history;
    history.lastCompleted = activity.lastCompleted;
    if (activity.lastCompletedBy) history.lastCompletedBy = activity.lastCompletedBy;
    if (activity.lastCompletedWorkOrderKey) history.lastCompletedWorkOrderKey = activity.lastCompletedWorkOrderKey;
    return history;
};

/**
 * The completion history is a fact about the building, not a plan decision:
 * activation keeps the active plan's `lastCompleted*` on every activity it
 * replaces unless the new one carries a later completion. This covers a draft
 * started from scratch and work orders completed while the draft was open.
 */
export const withCompletionHistory = (
    activity: MaintenanceActivity,
    previous: MaintenanceActivity | undefined
): MaintenanceActivity => {
    const kept = completionHistory(previous);
    if (!kept.lastCompleted) return activity;
    if (activity.lastCompleted && activity.lastCompleted >= kept.lastCompleted) return activity;
    const merged: MaintenanceActivity = { ...activity };
    delete merged.lastCompletedBy;
    delete merged.lastCompletedWorkOrderKey;
    return { ...merged, ...kept };
};
